import React, { FC, memo, useRef } from 'react'
import Nav, { NavHandle } from './Nav'
import { useNavLogic } from './useNavLogic'

type NavItemType = { identifier?: string; itemClass?: string } & Omit<
  React.ComponentProps<typeof Nav>,
  'key' | 'active'
>

interface MenuProps {
  mainNavItems?: Array<NavItemType>
  subNavItems?: Array<NavItemType>
  section?: string | null
  subSection?: string | null
  wrapperClass?: string
  mainNavClass?: string
  subNavClass?: string
}

const Menu: FC<MenuProps> = memo(props => {
  const {
    mainNavItems = [],
    subNavItems = [],
    section = null,
    subSection = null,
    wrapperClass = 'tabbed-nav',
    mainNavClass,
    subNavClass,
  } = props

  const scrollToIndex = (navRef: React.RefObject<NavHandle | null>, itemIndex: number) => {
    navRef.current?.scrollToIndex(itemIndex)
  }

  // Create refs with the correct types for Nav/SubNav components
  const mainNavRef = useRef<NavHandle | null>(null)
  const subNavRef = useRef<NavHandle | null>(null)

  // Combine navigation and scroll handling in a single hook per nav
  const mainNav = useNavLogic({
    items: mainNavItems,
    section: section,
    onNavigate: idOrIndex => {
      if (typeof idOrIndex === 'number') scrollToIndex(mainNavRef, idOrIndex)
    },
  })

  const subNav = useNavLogic({
    items: subNavItems,
    section: subSection,
    onNavigate: idOrIndex => {
      if (typeof idOrIndex === 'number') scrollToIndex(subNavRef, idOrIndex)
    },
  })

  return (
    <div className={wrapperClass}>
      {mainNavItems.length > 0 && (
        <Nav
          ref={mainNavRef}
          variant="main"
          getNavItemsContent={mainNav.getNavItemsContent}
          section={section}
          navClass={mainNavClass}
        />
      )}
      {subNavItems.length > 0 && (
        <Nav
          ref={subNavRef}
          variant="sub"
          getNavItemsContent={subNav.getNavItemsContent}
          section={subSection}
          navClass={subNavClass}
        />
      )}
    </div>
  )
})

Menu.displayName = 'Menu'
export default Menu
